/**
 * Workspace Surface Component
 * Phase 7: Workspace Surface Layer - Main Container
 * 
 * Primary surface that combines:
 * - PageTree sidebar for navigation
 * - ActiveWorkspace content area
 * - SurfaceLayout for responsive layout
 * 
 * This is the main workspace interface that users interact with.
 */

import React, { useEffect, useState } from 'react';
import SurfaceLayout from '../common/SurfaceLayout';
import { PageTree } from '../common/PageTree';
import { ActiveWorkspace } from './ActiveWorkspace';
import { useWorkspace } from '../../../core/workspace/WorkspaceContext';
import { useRouteSync } from '../../../core/router/useRouteSync';
import { useMantineColorScheme } from '@mantine/core';
import { Box, Stack, Group, ActionIcon, Tooltip } from '@mantine/core';
import { IconMenu2, IconX } from '@tabler/icons-react';
import { eventBus } from '../../../core/event-bus';

/**
 * Workspace Surface Props
 */
interface WorkspaceSurfaceProps {
  /** Phase number */
  phase?: number;
  /** Custom className */
  className?: string;
  /** Custom style */
  style?: React.CSSProperties;
  /** Enable/disable sidebar toggle on mobile */
  responsiveSidebarToggle?: boolean;
  /** Force specific layout (overrides responsive) */
  forceLayout?: 'mobile' | 'tablet' | 'desktop' | 'wide';
}

/**
 * Workspace Sidebar Header Component
 */
const WorkspaceSidebarHeader: React.FC<{
  collapsed?: boolean;
  onCollapse?: () => void;
}> = ({ collapsed = false, onCollapse }) => {
  return (
    <Box
      p="md"
      style={{
        borderBottom: '1px solid var(--mantine-color-gray-2)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}
    >
      <h2 style={{ margin: 0, fontSize: '16px', fontWeight: 600 }}>
        {!collapsed && 'Workspace'}
      </h2>
      {onCollapse && (
        <Tooltip label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}>
          <ActionIcon
            variant="subtle"
            size="sm"
            onClick={onCollapse}
            title={collapsed ? 'Expand' : 'Collapse'}
          >
            {collapsed ? <IconMenu2 size={16} /> : <IconX size={16} />}
          </ActionIcon>
        </Tooltip>
      )}
    </Box>
  );
};

/**
 * Workspace Sidebar Component
 * 
 * Combines sidebar header and page tree
 */
const WorkspaceSidebar: React.FC<{
  phase: number;
  currentPageId?: string;
  onPageSelect?: (pageId: string) => void;
  collapsed?: boolean;
  onCollapse?: () => void;
}> = ({ 
  phase, 
  currentPageId, 
  onPageSelect, 
  collapsed = false,
  onCollapse
}) => {
  return (
    <Stack gap={0} h="100%">
      <WorkspaceSidebarHeader collapsed={collapsed} onCollapse={onCollapse} />
      <Box
        style={{
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden'
        }}
      >
        <PageTree
          phase={phase}
          currentPageId={currentPageId}
          onPageSelect={onPageSelect}
        />
      </Box>
    </Stack>
  );
};

/**
 * Workspace Surface Component
 * 
 * Main container for the entire workspace interface.
 * Manages layout, sidebar visibility, and content routing.
 */
export const WorkspaceSurface: React.FC<WorkspaceSurfaceProps> = ({
  phase = 7,
  className = '',
  style = {},
  responsiveSidebarToggle = true,
  forceLayout
}) => {
  const { currentPageId, selectPage, sidebarCollapsed, toggleSidebar } = useWorkspace();
  const { navigateToPage } = useRouteSync();
  const { colorScheme } = useMantineColorScheme();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Handle page selection from sidebar - now with URL sync
  const handlePageSelect = async (pageId: string) => {
    await selectPage(pageId);
    navigateToPage(pageId);
  };

  // Handle sidebar toggle via event bus (from other components)
  useEffect(() => {
    const handler = () => toggleSidebar();
    eventBus.on('fc:sidebar:toggle', handler);
    return () => eventBus.off('fc:sidebar:toggle', handler);
  }, [toggleSidebar]);

  // Render sidebar content
  const sidebarContent = (
    <WorkspaceSidebar
      phase={phase}
      currentPageId={currentPageId}
      onPageSelect={handlePageSelect}
      collapsed={sidebarCollapsed}
      onCollapse={toggleSidebar}
    />
  );

  // Render main content
  const mainContent = (
    <Box h="100%" w="100%" style={{ display: 'flex', flexDirection: 'column' }}>
      <ActiveWorkspace />
    </Box>
  );

  return (
    <Box
      className={`workspace-surface ${className}`}
      h="100%"
      w="100%"
      style={{
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: colorScheme === 'dark' ? '#292929' : 'var(--mantine-color-white)',
        ...style
      }}
    >
      <SurfaceLayout
        sidebar={sidebarContent}
        main={mainContent}
        responsive={true}
        forceLayout={forceLayout}
        debugMode={false}
      />
    </Box>
  );
};

/**
 * Export component and context provider hook
 */
export { WorkspaceProvider, useWorkspace } from '../../../core/workspace/WorkspaceContext';
export type { WorkspaceSurfaceProps };

export default WorkspaceSurface;